import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';

/**
 *  Using forward ref and useImperativeHandle to consume child functions to the parent component.
 *  Mainly we are using for accessibility to achieve the arrow down/up features.
 */
const Dropdown = forwardRef(
  (
    {
      options,
      selected,
      handleSelect,
      isVisible,
      labelId,
      condensed,
    },
    ref
  ) => {
    const classes = [
      'axiom-select-input--dropdown dropdown',
      'option',
    ];

    if (isVisible) {
      classes.push('slideDown');
    }

    const ulRef = useRef();

    /**
     *  @param index: options index when selected (via arrow up/down, enter,
     * on base select when we open from parent/when we click onc select.)
     * @param triggerChange: param refers to the flag based on the selected
     * event only should reflect the changed element.
     */
    const itemSelect = (index, triggerToggle, key) => {
      let nextItem = ulRef.current.querySelector(
        'li[aria-selected=true]'
      );
      if (nextItem) {
        nextItem.removeAttribute('aria-selected');
      }
      nextItem = ulRef.current.children[index];
      nextItem.setAttribute('aria-selected', true);
      nextItem.focus();
      handleSelect(options[index], triggerToggle, key);
    };

    const handleKeyDown = (event) => {
      const key = event.which || event.keyCode;
      let nextItem = ulRef.current.querySelector(
        'li[aria-selected=true]'
      );
      let index = -1;
      if (!nextItem) {
        ulRef.current.focus();
        itemSelect(0, false, key);
        return null;
      }
      switch (key) {
        case 38:
        case 40:
        case 36:
        case 35:
          nextItem.removeAttribute('aria-selected');

          event.preventDefault();

          // Focus on UL for screen reader
          if (document.activeElement.tagName !== 'UL')
            ulRef.current.focus();
          if (key === 38) {
            do {
              nextItem = nextItem.previousElementSibling;
              if (!nextItem) {
                nextItem =
                  ulRef.current.children[
                    ulRef.current.children.length - 1
                  ];
              }
            } while (nextItem.classList.contains('disabled'));
          } else if (key === 40) {
            do {
              nextItem = nextItem.nextElementSibling;
              if (!nextItem) {
                nextItem =
                  ulRef.current.children[
                    ulRef.current.children.length -
                      ulRef.current.children.length
                  ];
              }
            } while (nextItem.classList.contains('disabled'));
          } else if (key === 35) {
            for (let i = 0; i < ulRef.current.children.length; i++) {
              nextItem =
                ulRef.current.children[
                  ulRef.current.children.length - 1 - i
                ];
              if (!nextItem.classList.contains('disabled')) {
                break;
              }
            }
          } else {
            for (let i = 0; i < ulRef.current.children.length; i++) {
              nextItem = ulRef.current.children[i];
              if (!nextItem.classList.contains('disabled')) {
                break;
              }
            }
          }
          /** update scroll focus in the options list with Arrow keys */
          if (
            ulRef.current.scrollHeight > ulRef.current.clientHeight
          ) {
            const scrollBottom =
              ulRef.current.clientHeight + ulRef.current.scrollTop;
            const eleBottom =
              nextItem.offsetTop + nextItem.offsetHeight;
            if (eleBottom > scrollBottom) {
              ulRef.current.scrollTop =
                eleBottom - ulRef.current.clientHeight;
            } else if (nextItem.offsetTop < ulRef.current.scrollTop) {
              ulRef.current.scrollTop = nextItem.offsetTop;
            }
          }

          if (nextItem) {
            nextItem.setAttribute('aria-selected', true);
            nextItem.focus();
          }

          index = Array.from(ulRef.current.children).indexOf(
            nextItem
          );
          itemSelect(index, false, key);
          nextItem.focus();
          break;
        case 13:
          nextItem.removeAttribute('aria-selected');
          // For Enter Key
          index = Array.from(ulRef.current.children).indexOf(
            nextItem
          );
          if (event.target.tagName === 'UL' && isVisible) {
            // collapse menu
            itemSelect(index, true, key);
          }
          break;
        case 9:
        case 27:
          nextItem.removeAttribute('aria-selected');

          // For Tab key
          index = Array.from(ulRef.current.children).indexOf(
            nextItem
          );
          if (event.target.tagName === 'UL' && isVisible) {
            // collapse menu
            itemSelect(index, true, key);
          }
          break;
        case 32:
          // For Spacebar
          index = Array.from(ulRef.current.children).indexOf(
            nextItem
          );
          itemSelect(index, false, key);
          break;
        default:
          break;
      }
      return true;
    };

    const setSelectedAria = () => {
      let nextItem = ulRef.current.querySelector(
        'li[aria-selected=true]'
      );
      // Focus on UL for screen reader
      ulRef.current.focus();
      if (!nextItem) {
        nextItem = ulRef.current.querySelector('li');
        ulRef.current.setAttribute(
          'aria-activedescendant',
          nextItem.getAttribute('id')
        );
        nextItem.setAttribute('aria-selected', true);
        nextItem.focus();
        return;
      }
      nextItem.setAttribute('aria-selected', true);
      ulRef.current.setAttribute(
        'aria-activedescendant',
        nextItem.getAttribute('id')
      );
      nextItem.focus();
    };

    const getSelectedAria = () => {
      const selectedOptionIndex = options.findIndex(
        (option) => option.value === selected.value
      );

      return selectedOptionIndex > -1
        ? `${`${labelId}_${selectedOptionIndex}`}`
        : null;
    };

    /**
     *  Following condition uses for the parent triggers the child functions.
     *  We have a parent key down function, it triggers when parent key press happened.
     *  We have condition based on dropdown opened/closed and also first time dropdown
     * opened to select first one as default.
     */
    useImperativeHandle(ref, () => ({
      handleParentKeyDown(event, isDropdownVisible) {
        const key = event.which || event.keyCode;
        if (isDropdownVisible) {
          handleKeyDown(event);
        } else if (
          key === 13 ||
          key === 38 ||
          key === 40 ||
          key === 32
        ) {
          setSelectedAria();
        }
      },
    }));

    const maxOptionCount = 5;
    const heightStandard = 41.5;
    const heightCondensed = 37.5;
    const dropdownPadding = 8;
    const dropdownBorder = 2;
    const optionHeight = condensed ? heightCondensed : heightStandard;
    let heightDropDown =
      (options.length > maxOptionCount
        ? maxOptionCount * optionHeight
        : options.length * optionHeight) +
      dropdownPadding * 2 +
      dropdownBorder +
      1;
    if (!isVisible) {
      heightDropDown = 0;
    }

    useEffect(() => {
      const domOptionlist = Array.from(ulRef.current.children);
      const selectedOption = options.find(
        (option) => option.selected
      );

      domOptionlist.forEach((domOption) => {
        const isSelected =
          typeof selectedOption !== 'undefined'
            ? domOption.dataset.value === selectedOption.value
            : false;

        if (isSelected) {
          ulRef.current.setAttribute(
            'aria-activedescendant',
            domOption.id
          );
        }

        domOption.setAttribute('aria-selected', isSelected);
      });
    }, [options]);

    return (
      <ul
        className={classes.join(' ')}
        role='listbox'
        ref={ulRef}
        tabIndex='-1'
        aria-label='list'
        aria-labelledby={labelId}
        aria-activedescendant={getSelectedAria()}
        onKeyDown={(e) => handleKeyDown(e)}
        style={{
          maxHeight: `${heightDropDown}px`,
          minHeight: heightDropDown,
        }}
        id={`${labelId}_option-list`}
        data-testid='option-list'
      >
        {options.map((opt, index) => (
          <li
            role='option'
            data-value={opt.value}
            aria-selected={opt.selected ? 'true' : 'false'}
            aria-disabled={opt.disabled ? true : undefined}
            className={opt.disabled ? 'items disabled' : 'items'}
            key={`${opt.name + index}`}
            id={`${`${labelId}_${index}`}`}
            onClick={(e) => itemSelect(index, true, e.keyCode)}
            onKeyDown={(e) => itemSelect(index, true, e.keyCode)}
          >
            {opt.name}
          </li>
        ))}
      </ul>
    );
  }
);

Dropdown.displayName = 'Dropdown';

Dropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
      selected: PropTypes.bool,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  selected: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
    selected: PropTypes.bool,
    disabled: PropTypes.bool,
  }),
  isVisible: PropTypes.bool,
  handleSelect: PropTypes.func.isRequired,
  labelId: PropTypes.string.isRequired,
  condensed: PropTypes.bool,
};

Dropdown.defaultProps = {
  isVisible: null,
  condensed: false,
  selected: {
    name: '',
    value: undefined,
  },
};

export default Dropdown;
